import { useState, useEffect, useCallback, useRef } from "react";
import {
  getMaaraaikaistarkastusVersiotByKiinteisto,
  getMaaraaikaistarkastusVersio,
  createMaaraaikaistarkastusVersio,
  deleteMaaraaikaistarkastusVersio,
  createMaaraaikaistarkastusRyhma,
  updateMaaraaikaistarkastusRyhma,
  deleteMaaraaikaistarkastusRyhma,
  createMaaraaikaistarkastusRivi,
  updateMaaraaikaistarkastusRivi,
  deleteMaaraaikaistarkastusRivi,
} from "../../api/api";

// Live-DB sync for MaaraaikaistarkastusTaulukko.jsx's Taloyhtiokortti-tab
// usage — modeled on usePeitepaksuusV2.js's established patterns (stale-
// response sequence guard, in-flight save counter), generalized to this
// table's two levels (groups -> rows) and its versioning model (only the
// current/newest version is ever editable; see the KTP_demo plan's
// "Versioning" section for why there's no is_current flag client-side
// either — isCurrent always comes from the backend's own GET response).

const ROW_FIELDS = [
  "nimi", "saadosviite", "vali", "sovelletaanko", "viimeisin",
  "seuraava", "vastuutaho", "tila", "dokumentti", "toimenpiteet",
];

function pickRowFields(row) {
  const out = {};
  for (const f of ROW_FIELDS) if (row[f] !== undefined) out[f] = row[f];
  return out;
}

const apiErrorMessage = (e) => e?.response?.data?.error || e.message;

// A row/group created THIS session keeps its component-generated
// crypto.randomUUID() as its React key for the rest of the mount's
// lifetime (the component never resyncs from the `data` prop after mount,
// so there's no way to swap it for the real id without remounting). This
// resolver maps that local id to the real DB id once the create POST
// resolves, and lets any update/delete that arrives for a still-in-flight
// create simply await the same promise instead of racing it.
function makeIdResolver() {
  const idMap = new Map();
  const pending = new Map();
  return {
    async resolve(id) {
      if (idMap.has(id)) return idMap.get(id);
      if (pending.has(id)) return pending.get(id);
      return id; // already a real id (loaded from the backend, not created locally)
    },
    track(localId, promise) {
      const tracked = promise.then(realId => { idMap.set(localId, realId); return realId; });
      pending.set(localId, tracked);
      tracked.finally(() => pending.delete(localId));
      return tracked;
    },
  };
}

export default function useMaaraaikaistarkastusData(kiinteistoId) {
  const [versions, setVersions] = useState([]);
  const [activeVersioId, setActiveVersioId] = useState(null);
  const [isCurrent, setIsCurrent] = useState(true);
  const [data, setData] = useState(null); // { groups: [...] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const pendingSavesRef = useRef(0);
  const beginSave = useCallback(() => { pendingSavesRef.current += 1; setSaving(true); }, []);
  const endSave = useCallback(() => {
    pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    if (pendingSavesRef.current === 0) setSaving(false);
  }, []);

  // Brief "Tallennettu" confirmation after a successful write — called
  // explicitly on each success path below (not from endSave, which also
  // runs on failure), same visible-system-status idea as
  // DynamicPTSTaulu.jsx's own flashSaved().
  const [savedFlash, setSavedFlash] = useState(false);
  const savedFlashTimerRef = useRef(null);
  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    clearTimeout(savedFlashTimerRef.current);
    savedFlashTimerRef.current = setTimeout(() => setSavedFlash(false), 1800);
  }, []);

  const groupIds = useRef(makeIdResolver());
  const rowIds = useRef(makeIdResolver());

  // Discards a load's response if a newer one has since been kicked off
  // (switching versions quickly, or re-mounting on a kiinteistö change)
  // — same idiom as usePeitepaksuusV2.refresh's refreshSeqRef.
  const loadSeqRef = useRef(0);

  const loadVersion = useCallback(async (versioId) => {
    const seq = ++loadSeqRef.current;
    const res = await getMaaraaikaistarkastusVersio(versioId);
    if (seq !== loadSeqRef.current) return;
    setData({ groups: res.data.groups });
    setIsCurrent(res.data.isCurrent);
    setActiveVersioId(res.data.id);
  }, []);

  useEffect(() => {
    if (!kiinteistoId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    groupIds.current = makeIdResolver();
    rowIds.current = makeIdResolver();
    getMaaraaikaistarkastusVersiotByKiinteisto(kiinteistoId)
      .then(async (res) => {
        if (cancelled) return;
        setVersions(res.data);
        const current = res.data[0];
        if (current) await loadVersion(current.id);
      })
      .catch(e => { if (!cancelled) setError(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kiinteistoId, loadVersion]);

  // Switching to a historical version re-mounts the table with that
  // version's data (Taloyhtiokortti.js keys the component on
  // activeVersioId too, since the component itself never resyncs from a
  // changed `data` prop after its first mount).
  const switchVersion = useCallback((versioId) => {
    setLoading(true);
    setError(null);
    loadVersion(versioId)
      .catch(e => setError(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [loadVersion]);

  // "Luo uusi versio" — clones the current version's full tree server-side
  // (not a blank slate), then switches straight to viewing/editing it.
  const createVersion = useCallback(async (label) => {
    if (!kiinteistoId) return;
    beginSave();
    try {
      const res = await createMaaraaikaistarkastusVersio({ id_kiinteisto: kiinteistoId, label });
      const versionsRes = await getMaaraaikaistarkastusVersiotByKiinteisto(kiinteistoId);
      setVersions(versionsRes.data);
      groupIds.current = makeIdResolver();
      rowIds.current = makeIdResolver();
      setData({ groups: res.data.groups });
      setIsCurrent(true);
      setActiveVersioId(res.data.id);
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [kiinteistoId, beginSave, endSave, flashSaved]);

  // Deletes any version (current or historical) — the backend refuses to
  // delete a kiinteistö's last remaining one (409). If the version being
  // viewed right now is the one deleted, switches straight to whatever is
  // current afterward so the UI never ends up pointed at a version that no
  // longer exists.
  const deleteVersion = useCallback(async (versioId) => {
    beginSave();
    try {
      await deleteMaaraaikaistarkastusVersio(versioId);
      const versionsRes = await getMaaraaikaistarkastusVersiotByKiinteisto(kiinteistoId);
      setVersions(versionsRes.data);
      if (versioId === activeVersioId) {
        const next = versionsRes.data[0];
        if (next) await loadVersion(next.id);
      }
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [kiinteistoId, activeVersioId, loadVersion, beginSave, endSave, flashSaved]);

  const onGroupAdd = useCallback((group) => {
    beginSave();
    const promise = createMaaraaikaistarkastusRyhma({
      id_versio: activeVersioId,
      title: group.title,
      rows: (group.rows || []).map(pickRowFields),
    })
      .then(res => {
        (group.rows || []).forEach((localRow, idx) => {
          const realRow = res.data.rows?.[idx];
          if (realRow) rowIds.current.track(localRow.id, Promise.resolve(realRow.id));
        });
        flashSaved();
        return res.data.id;
      })
      .catch(e => { setError(apiErrorMessage(e)); throw e; })
      .finally(endSave);
    groupIds.current.track(group.id, promise);
  }, [activeVersioId, beginSave, endSave, flashSaved]);

  const onGroupTitleChange = useCallback(async (groupId, title) => {
    beginSave();
    try {
      const realId = await groupIds.current.resolve(groupId);
      await updateMaaraaikaistarkastusRyhma(realId, { title });
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  const onGroupDelete = useCallback(async (groupId) => {
    beginSave();
    try {
      const realId = await groupIds.current.resolve(groupId);
      await deleteMaaraaikaistarkastusRyhma(realId);
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  const onRowAdd = useCallback(async (groupId, row) => {
    beginSave();
    try {
      const realGroupId = await groupIds.current.resolve(groupId);
      const promise = createMaaraaikaistarkastusRivi({ id_ryhma: realGroupId, ...pickRowFields(row) })
        .then(res => res.data.id);
      rowIds.current.track(row.id, promise);
      await promise;
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  const onRowFieldChange = useCallback(async (groupId, rowId, field, value) => {
    beginSave();
    try {
      const realId = await rowIds.current.resolve(rowId);
      await updateMaaraaikaistarkastusRivi(realId, { [field]: value });
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  const onRowDelete = useCallback(async (groupId, rowId) => {
    beginSave();
    try {
      const realId = await rowIds.current.resolve(rowId);
      await deleteMaaraaikaistarkastusRivi(realId);
      flashSaved();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      endSave();
    }
  }, [beginSave, endSave, flashSaved]);

  return {
    data, loading, saving, error, savedFlash,
    versions, activeVersioId, isCurrent,
    switchVersion, createVersion, deleteVersion,
    onGroupAdd, onGroupTitleChange, onGroupDelete,
    onRowAdd, onRowFieldChange, onRowDelete,
  };
}
